import { z } from "zod";
import { journeyStepsCopyMapSchema } from "./journey-step-copy";
import { prescriptionRenewalSchema } from "./prescription-renewal";
import { journeyStepsSchema } from "./journey-steps";
import { mainStepsSchema } from "./main-steps";
import {
  journeyStateSchema,
  treatmentPhaseSchema,
} from "./patient-journey-state";
import { localeSchema } from "./common";
import { productFeedbackConsentStateSchema } from "./product-feedback-consent";
import {
  prescriptionCycleStateSchema,
  type PrescriptionCycleState,
} from "./prescription-cycle";
import { tenantIdSchema } from "./tenant";
import { subscriptionPlanSchema } from "./subscription-plan";
import {
  completionGroupSchema,
  prescriptionTransferReviewStatusSchema,
} from "./prescription-transfer-review";

export const patientIdSchema = z.string().uuid();
export type PatientId = z.infer<typeof patientIdSchema>;

export const patientSchema = z.object({
  id: patientIdSchema,
  tenantId: tenantIdSchema,
  partnerClinicId: z.string(),
  personalNumber: z.string(), // SE personnummer, format YYYYMMDD-XXXX
  email: z.string().email().nullable(),
  preferredLocale: localeSchema,
  webdocPatientRef: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type Patient = z.infer<typeof patientSchema>;

// CAR-7 / CAR-20 — `/patient-api/v1/me`. The single home-screen
// rollup for the mobile patient app: who they are, where they are in
// treatment, what's due next. Wire shape (strings for dates) so the
// mobile client + the API serializer agree.

export const subscriptionStatusSchema = z.enum([
  "none",
  "trialing",
  "active",
  "past_due",
  "canceled",
  "incomplete",
  "incomplete_expired",
  "unpaid",
  "paused",
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

// The monthly check-in's (F-05's) number. Month 1, 3, 6, 9 or 12 from
// baseline on the month schedule; on the anchored schedule (TES-1425) the
// n-th F-05, one every fourth treatment week, so any positive integer.
export const milestoneMonthIndexSchema = z.number().int().positive();
export type MilestoneMonthIndex = z.infer<typeof milestoneMonthIndexSchema>;

// treatmentStateSchema lives in treatment.ts; we inline the literal
// union here to avoid an import cycle (treatment.ts → patient.ts via
// patientIdSchema). The two definitions must stay in sync.
const meResponseTreatmentStateSchema = z.enum([
  "onboarding",
  "active",
  "between_cycles",
  "paused",
  "ended",
]);
export type MeResponseTreatmentState = z.infer<
  typeof meResponseTreatmentStateSchema
>;

// CAR-109 — server-computed onboarding ladder. The mobile Home tab reads
// `onboarding.step` instead of re-deriving the sequence client-side, so the
// gate ORDER lives in one place and is integration-tested.
//
// Resolved payment model (CAR-108): the subscription no longer gates *entry*.
// A newly-identified patient reaches the medical intake with no subscription;
// the subscription is collected later (via a doctor-sent web link) and gates
// only ONGOING treatment/medication. New order:
//   intake (1) → consult + fee (2) → labs (3) → subscription/activation (4).
// `needs_renewal` sits outside the ladder (a recurring loop, top priority);
// `active` is the terminal "done" state (no card).
//
// NOTE: consult-fee (CAR-114) and treatment activation (CAR-119) refine the
// later steps; `awaiting_labs` is now reachable (CAR-117) — once a lab order
// exists for a patient whose treatment isn't active yet, the labs step lights
// up. The labs signal is `labsOrdered` (a LabOrder exists), surfaced by /me.
export const onboardingStepSchema = z.enum([
  "needs_renewal",
  // TES-394 — F-01, "Ellas första frågor", is the REQUIRED first app
  // step: it blocks intake and everything after until submitted (or a linked
  // web-quiz lead already answered it).
  "needs_f01",
  "needs_intake",
  "intake_failed",
  "awaiting_consult",
  "awaiting_labs",
  "needs_subscription",
  "active",
]);
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

// CAR-116 — the onboarding ladder as a discrete task list ("where am I /
// what's next"). The keys are the four patient-facing onboarding tasks in
// order; the home Task surface renders them with the active one carrying the
// CTA and earlier ones checked off. `attention` is the failed-intake case.
export const onboardingTaskKeySchema = z.enum([
  "f01",
  "intake",
  "consult",
  "labs",
  "subscription",
]);
export type OnboardingTaskKey = z.infer<typeof onboardingTaskKeySchema>;

export const onboardingTaskStatusSchema = z.enum([
  "done",
  "active",
  "upcoming",
  "attention",
]);
export type OnboardingTaskStatus = z.infer<typeof onboardingTaskStatusSchema>;

export const onboardingTaskSchema = z.object({
  key: onboardingTaskKeySchema,
  status: onboardingTaskStatusSchema,
});
export type OnboardingTask = z.infer<typeof onboardingTaskSchema>;

export const onboardingSchema = z.object({
  step: onboardingStepSchema,
  // 1-based position in the patient-facing ladder; 0 for off-ladder states
  // (needs_renewal) and `total` for the terminal `active` state.
  index: z.number().int(),
  total: z.number().int(),
  // CAR-116 — the full task ladder with per-task status, so the home Task
  // surface can show progress + next action without re-deriving the order.
  tasks: z.array(onboardingTaskSchema),
});
export type Onboarding = z.infer<typeof onboardingSchema>;

// The fixed task ladder, in order. Position (1-based) doubles as the gate
// index. Completion of the later tasks (labs, activation) is refined by
// CAR-117/119; today it follows directly from the derived step.
const ONBOARDING_TASK_LADDER: OnboardingTaskKey[] = [
  "f01",
  "intake",
  "consult",
  "labs",
  "subscription",
];

// The ladder position the patient is currently working on (1..5). `5` means
// the onboarding ladder is complete (active treatment or the renewal loop).
function currentLadderPosition(step: OnboardingStep): number {
  switch (step) {
    case "needs_f01":
      return 1;
    case "needs_intake":
    case "intake_failed":
      return 2;
    case "awaiting_consult":
      return 3;
    case "awaiting_labs":
      return 4;
    case "needs_subscription":
      return 5;
    case "active":
    case "needs_renewal":
      return 6;
  }
}

function buildOnboardingTasks(step: OnboardingStep): OnboardingTask[] {
  const current = currentLadderPosition(step);
  return ONBOARDING_TASK_LADDER.map((key, i) => {
    const position = i + 1;
    let status: OnboardingTaskStatus;
    if (step === "intake_failed" && key === "intake") {
      status = "attention";
    } else if (position < current) {
      status = "done";
    } else if (position === current) {
      status = "active";
    } else {
      status = "upcoming";
    }
    return { key, status };
  });
}

type OnboardingInput = {
  cycleState: PrescriptionCycleState | null;
  // TES-394 — F-01 answered: an in-app f01 submission OR a linked web-quiz
  // lead (the web funnel already asked the same questions — never re-ask).
  f01Done: boolean;
  hasIntake: boolean;
  intakeClean: boolean;
  // TES-675 / CP-13 — an uncleared MedicalRiskFlag.
  // Required for the same reason as on JourneyStepsInput: forgetting it puts
  // the ordinary rejection copy back in front of an acute safety signal.
  acuteSafetyActive: boolean;
  treatmentState: MeResponseTreatmentState | null;
  entitled: boolean;
  // CAR-117 — the labs signal: a LabOrder exists for the patient. Splits the
  // "intake clean, treatment not active" band into awaiting_consult (no order
  // yet) vs awaiting_labs (doctor has ordered bloods, patient must go give
  // them before treatment can be activated).
  labsOrdered: boolean;
};

// The gate order — the single source of truth for the sequence. Returns the
// active step + its ladder index; deriveOnboarding wraps this with the task
// list so step and tasks can never disagree.
function resolveOnboardingStep(
  input: OnboardingInput,
  total: number,
): { step: OnboardingStep; index: number } {
  // Renewal is the most time-sensitive and lives in the recurring loop, not
  // the onboarding ladder.
  if (input.cycleState === "expiring_soon") {
    return { step: "needs_renewal", index: 0 };
  }
  // Step 1 — F-01 ("Ellas första frågor"). The app-first suitability quiz
  // blocks the rest of the ladder (TES-394); a web-funnel patient's
  // linked lead counts as done.
  if (!input.f01Done) {
    return { step: "needs_f01", index: 1 };
  }
  // Step 2 — intake. Reachable with NO subscription (the relocated gate).
  if (!input.hasIntake) {
    return { step: "needs_intake", index: 2 };
  }
  // TES-675 / CP-13 — `intake_failed` renders
  // "Vi kan tyvärr inte erbjuda behandling" +
  // "kontakta support om du har frågor", and HomeScreen
  // highlights the plan section when it is the active step. As the answer to
  // "jag har tankar på att inte vilja leva" that is the exact rendering
  // Reported: the product's most acute signal shown through its most
  // ordinary rejection template, pointing at a support inbox.
  //
  // In the acute state the ladder falls through to the ordinary post-intake
  // band instead. Her booked meeting is still real and still hers; what
  // changes is that Home stops announcing a refusal. §2's tier-1 acute card
  // owns the screen regardless, and it carries 112 and 1177.
  if (!input.intakeClean && !input.acuteSafetyActive) {
    return { step: "intake_failed", index: 2 };
  }
  // Treatment activated by the doctor. Ongoing treatment/medication needs the
  // (web) subscription — this is where the gate now lives. Entitled → done.
  if (input.treatmentState === "active") {
    return input.entitled
      ? { step: "active", index: total }
      : { step: "needs_subscription", index: 5 };
  }
  // Intake clean, treatment not yet active: the consult → labs → approval band.
  // CAR-117 — once the doctor has ordered bloods, the patient's next action is
  // the lab visit, not the consult: surface awaiting_labs (step 3). Before that
  // it reads as awaiting_consult (step 2). CAR-114 refines the consult step.
  if (input.labsOrdered) {
    return { step: "awaiting_labs", index: 4 };
  }
  return { step: "awaiting_consult", index: 3 };
}

// Pure derivation — shared by the API (/me) and its tests. Takes only the
// signals /me already loads.
export function deriveOnboarding(input: OnboardingInput): Onboarding {
  const total = 5; // f01, intake, consult, labs, subscription
  const { step, index } = resolveOnboardingStep(input, total);
  return { step, index, total, tasks: buildOnboardingTasks(step) };
}

// TES-460 — the prescription-transfer review as the PATIENT may see it:
// status, what she was asked to redo, and the doctor's message — which is
// patient-facing only for a completion request ("komplettering", decision
// 3, TES-458); for every other outcome `reason` is internal and
// `patientMessage` is null. Latest review wins, null for a patient who is
// not on the transfer track or has no review yet.
export const meTransferReviewSchema = z.object({
  status: prescriptionTransferReviewStatusSchema,
  requestedCompletionGroups: z.array(completionGroupSchema),
  completionRequestedAt: z.string().nullable(),
  decidedAt: z.string().nullable(),
  patientMessage: z.string().nullable(),
  // TES-734 — "fortsatt vårdväg" after a cannot_transfer; null otherwise.
  carePath: z.string().nullable().optional(),
});
export type MeTransferReview = z.infer<typeof meTransferReviewSchema>;

// TES-504 — the three e-mail states, derived from nullability on the
// server: no address / address awaiting the link / confirmed.
export const patientEmailStatusSchema = z.enum([
  "missing",
  "unverified",
  "verified",
]);
export type PatientEmailStatus = z.infer<typeof patientEmailStatusSchema>;

// TES-754 (Ö-151 ✅ 2026-09-16, Ö-152 ✅ 2026-09-17) — the real doctor behind
// the patient's care team card.
//
// This REVERSES a deliberate position. TES-215 made the care-team identities
// the tenant's generic personas — avatar plus role label, never a real
// clinician's name. Emelie has now decided the opposite: the patient should
// see who is actually responsible for her care, so nobody should "fix" this
// back. (The screen that carried the old stance, CareTeamHeader.tsx, was
// deleted in TES-714 — it had been unreachable since the chat split.)
//
// Resolution order is Emelie's: the responsible doctor on the active care
// case first, the doctor from the most recent relevant meeting second, and
// "Din läkare" when neither exists — NOT the generic persona.
//
// `source` travels so the app (and anyone debugging a support ticket) can
// tell which rung answered. Null means neither did.
export const careTeamDoctorSourceSchema = z.enum([
  // TES-804 — an EXPLICIT handover, and the strongest rung: someone chose
  // this doctor rather than the relationship arising from the work. Listed
  // first because that is the resolution order.
  "assignment",
  "care_relationship",
  "meeting",
]);
export type CareTeamDoctorSource = z.infer<typeof careTeamDoctorSourceSchema>;

export const careTeamDoctorSchema = z.object({
  /**
   * The clinician's real name. No title travels with it: there is no
   * per-employee title column anywhere — the only `title` in the codebase is
   * the tenant persona's generic role label ("Läkare"), which is exactly the
   * thing this change stops using as an identity. The app keeps rendering a
   * role label from its own locale; only the NAME is now real.
   */
  displayName: z.string(),
  source: careTeamDoctorSourceSchema,
  /**
   * TES-719 — the doctor's Employee id, so the app can show HER portrait
   * (`careTeamPortraitKey(employeeId)`) next to her name instead of the
   * tenant's single role slot. Optional so a response from an API before
   * TES-719 still parses; the app then shows its bundled silhouette.
   */
  employeeId: z.string().optional(),
  /**
   * TES-719 — the media key of her portrait, resolved by the server: her own
   * once uploaded, else the tenant's doctor role slot ("care_team.doctor"),
   * null when neither exists. Absent from an older API; the app then uses
   * the role slot itself.
   */
  avatarMediaKey: z.string().nullable().optional(),
});
export type CareTeamDoctor = z.infer<typeof careTeamDoctorSchema>;

export const meResponseSchema = z.object({
  patient: z.object({
    id: z.string().uuid(),
    preferredLocale: localeSchema,
    partnerClinicId: z.string(),
    // TES-504 — the patient's own address (she is the caller) and where
    // it stands, so the app's contact step can skip, nag, or offer resend.
    email: z.string().nullable(),
    emailStatus: patientEmailStatusSchema,
    // TES-504 — a verified patient's new address waiting for its link; the
    // verified `email` stays in force until then. null = no change pending.
    pendingEmail: z.string().nullable(),
    // TES-1106 — her product-feedback consent as it stands (null = never
    // chosen = no consent), for the Profil checkbox.
    productFeedbackConsent: productFeedbackConsentStateSchema.nullable(),
    // TES-430 (Ö-86) — the age gate, from BankID's personal number: under
    // 18 is stopped before F-01, booking and payment. The server decides;
    // the app only shows the stop page.
    under18: z.boolean(),
    // TES-298/QA — gates the app's temporary "Testläge" controls. Real
    // (BankID) patients are never test patients.
    isTest: z.boolean(),
  }),
  treatment: z
    .object({
      id: z.string().uuid(),
      state: meResponseTreatmentStateSchema,
      startedAt: z.string(),
      baselineCapturedAt: z.string().nullable(),
      baselineWeightKg: z.number().nullable(),
      baselineWaistCm: z.number().nullable(),
      baselineHipCm: z.number().nullable(),
    })
    .nullable(),
  currentCycle: z
    .object({
      id: z.string().uuid(),
      state: prescriptionCycleStateSchema,
      cycleNumber: z.number().int(),
      expiresAt: z.string().nullable(),
      // TES-202 — the doctor-authored prescription label (medication +
      // dosage, e.g. "Wegovy 0.25mg/wk"). Empty until a cycle is approved;
      // the app surfaces it as the Treatment Plan View only when non-empty.
      dose: z.string(),
      // TES-1159 (Emelie 2026-10-07 evening, decision 4a) — the ordination's
      // parts for Home's "Din behandling" card, each straight from the ONE
      // ordination source (the live prescription cycle), never parsed out of
      // `dose`. Null when the cycle predates TES-636 or a part is missing or
      // malformed: then nothing derived is shown. Optional so an older
      // server reads as "not sent"; `dose` stays for builds before 2.0.7.
      //   medication — "Mounjaro"
      //   strength   — the prescribed dose, "2,5 mg"
      //   frequency  — the dosing interval as the doctor wrote it,
      //                "en gång i veckan"
      //   nextDoseOn — the next planned dose DATE (YYYY-MM-DD, Stockholm),
      //                today included, when it can be computed safely: a
      //                once-a-week ordination, a registered first dose, an
      //                active (not paused) treatment. Otherwise null. Planned
      //                from the first dose date + 7-day steps, so a later
      //                dose-day task can key on it.
      medication: z.string().nullable().optional(),
      strength: z.string().nullable().optional(),
      frequency: z.string().nullable().optional(),
      nextDoseOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    })
    .nullable(),
  latestIntake: z
    .object({
      id: z.string().uuid(),
      version: z.number().int(),
      submittedAt: z.string(),
      hardStopCount: z.number().int(),
      flagCount: z.number().int(),
      clean: z.boolean(),
    })
    .nullable(),
  nextMilestone: z
    .object({
      dueAt: z.string(),
      monthIndex: milestoneMonthIndexSchema,
    })
    .nullable(),
  yearCycle: z
    .object({
      baselineCapturedAt: z.string(),
      retestDueAt: z.string(),
    })
    .nullable(),
  subscription: z.object({
    status: subscriptionStatusSchema,
    entitled: z.boolean(),
    currentPeriodEnd: z.string().nullable(),
    cancelAtPeriodEnd: z.boolean(),
    // TES-479 (Ö-3) — the doctor has cleared this patient to subscribe: a
    // SubscriptionLink exists for her and has not expired. This is what
    // /checkout gates on, and it is deliberately a property of the PATIENT,
    // not of the URL she arrived by.
    //
    // Why not "an unused link": CAR-115's `validate` marks `usedAt` when
    // /subscribe is merely LOADED, before she has authenticated. Gating on
    // unused would therefore reject every real patient — and burning the
    // link on page load already stranded anyone who abandoned at BankID.
    // Keyed on expiry instead, the 72h window is the doctor's actual intent
    // and survives a failed identify.
    invited: z.boolean(),
    // TES-924 — the plan a checkout started now would charge: the plan on
    // the newest subscription link that names one. Null when no link names a
    // plan, in which case checkout charges the core price (Ella Uppstart).
    // The web /checkout page shows this plan's label and price.
    checkoutPlan: subscriptionPlanSchema.nullable(),
    // TES-755 — the end date of a cancellation already placed (Stripe's
    // `cancel_at`). Separate from `cancelAtPeriodEnd`: a binding period + a
    // notice period give a date that is rarely the end of the current
    // period. Null = not cancelled.
    scheduledCancelAt: z.string().nullable(),
  }),
  // TES-591 — the access classes as the server sees them.
  // `subscriptionContent` is the ONLY one that can be false: care and the
  // journey history are always open once the account exists. The app renders
  // "Stöd"/"Hälsa" locked from this; the API enforces it regardless.
  access: z.object({
    care: z.literal(true),
    journeyHistory: z.literal(true),
    subscriptionContent: z.boolean(),
  }),
  // CAR-109 — server-computed onboarding step (the gate ladder, new order).
  onboarding: onboardingSchema,
  // TES-398 — "Dina steg": the typed progression surface. Derived from the
  // same signals as `onboarding` (which stays for back-compat consumers);
  // this adds step types + the recurring/at-goal catalogs.
  journeySteps: journeyStepsSchema,
  // TES-408 — Drive/admin-owned step copy, locale-resolved; only steps with
  // a PUBLISHED row appear. The app falls back to bundled locales per key.
  journeyStepsCopy: journeyStepsCopyMapSchema,
  // TES-279 (spec §1) — the canonical stored journey state + the two derived
  // views on it. treatmentPhase derives from the first cycle's activation
  // timestamp; currentMedicalCycleDay = today − active cycle start + 1
  // (null without an active cycle).
  journeyState: journeyStateSchema,
  treatmentPhase: treatmentPhaseSchema,
  currentMedicalCycleDay: z.number().int().nullable(),
  // TES-460 — see meTransferReviewSchema.
  transferReview: meTransferReviewSchema.nullable(),
  // TES-754 (Ö-151/Ö-152) — see careTeamDoctorSchema.
  careTeamDoctor: careTeamDoctorSchema.nullable(),
  // TES-1634 — "Nästa receptförnyelse", which replaces Dina steg under
  // active treatment (prescription-renewal.ts). Null before the first dose
  // with Ella and outside active treatment. Optional: an older server sends
  // nothing, and an older app ignores the field.
  prescriptionRenewal: prescriptionRenewalSchema.nullable().optional(),
  // TES-1656 — the four main steps to the doctor's decision, with her
  // sub-steps (main-steps.ts). Sent only to a build at
  // CLIENT_CONTRACT_CHANGES.fourMainSteps, and only before the treatment on
  // the new-treatment track; absent everywhere else.
  mainSteps: mainStepsSchema.optional(),
  // TES-351: `flowCards` (TES-163) removed — the TES-311 home rework retired
  // the home_card feed; the field was computed on every /me with no consumer.
});
export type MeResponse = z.infer<typeof meResponseSchema>;

// TES-755 — cancellation with a binding period (3 months) + a notice period
// (1 month).
//
// Happens exclusively on the web (Simon 2026-09-17); the app deliberately has
// no cancellation path. `/manage` fetches the preview BEFORE the
// confirmation, so the patient sees her actual end date before she cancels —
// a binding period discovered afterwards is no disclosure.
export const cancellationPreviewSchema = z.object({
  // When the subscription and access actually end. `null` for subscriptions
  // without a binding or notice period (Ö-96) whose period end we have not
  // mirrored: then the answer is "at the end of the current period", and
  // Stripe owns the day. The copy must handle both cases.
  endsAt: z.string().nullable(),
  // End of the binding period, null for subscriptions without one (Ö-96).
  bindingEndsAt: z.string().nullable(),
  // What decided the date — drives which sentence is shown.
  //   binding     the binding period has not ended yet
  //   notice      the notice period governs
  //   period_end  old terms: the next renewal does not happen
  reason: z.enum(["binding", "notice", "period_end"]),
  minTermMonths: z.number().int().nullable(),
  noticeMonths: z.number().int().nullable(),
  alreadyScheduled: z.boolean(),
  scheduledCancelAt: z.string().nullable(),
});
export type CancellationPreview = z.infer<typeof cancellationPreviewSchema>;

export const cancellationResultSchema = z.object({
  endsAt: z.string().nullable(),
  bindingEndsAt: z.string().nullable(),
  // true = a cancellation already existed; the date is NEVER recomputed,
  // otherwise every new button press would push the end a month further out.
  alreadyScheduled: z.boolean(),
});
export type CancellationResult = z.infer<typeof cancellationResultSchema>;

// TES-649 — PUT /me/fcm-token. A device push token: APNs tokens are 64 hex
// characters and FCM's a few hundred, so 4096 is a bound, not a format.
export const registerPushTokenSchema = z.object({
  token: z.string().min(1).max(4096),
  platform: z.enum(["ios", "android"]),
});
export type RegisterPushTokenRequest = z.infer<typeof registerPushTokenSchema>;

// TES-649 — POST /me/delete. `orderRef` stays optional here on purpose: a
// missing one is refused by the BankID re-auth check with a 401, the same
// answer as a stale one, and the parse must not turn that into a 400.
export const deleteAccountSchema = z
  .object({ orderRef: z.string().optional() })
  .default({});
export type DeleteAccountRequest = z.infer<typeof deleteAccountSchema>;
